import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { offlineRequestsKeys } from './queries/useOfflineRequestsQuery';
import { queryClient } from '../lib/queryClient';
import { fieldPermissionService } from '../services/auth/fieldPermissionService';
import { runtimeFeatureFlagsService } from '../services/runtimeFeatureFlagsService';
import { teamInformationService } from '../services/teamInformationService';
import { AppRole } from '../types/auth';

/**
 * Button permissions interface
 * All permissions are simple booleans: true = show button, false = hide button
 */
export interface ButtonPermissions {
  /** Show Save button (also controls Edit button visibility) */
  canShowSave: boolean;

  /** Show Edit button (uses Save permissions) */
  canShowEdit: boolean;

  /** Show Delete button */
  canShowDelete: boolean;

  /** Show Submit button (stage transition) */
  canShowSubmit: boolean;

  /** Show Recording button (audio transcription) */
  canShowRecording: boolean;

  /** Show Text Intake button (paste or upload text for extraction) */
  canShowTextIntake: boolean;

  /** Show Link Deal button (deal registration linking) */
  canShowLinkDeal: boolean;

  /** Show Restore button (version-history restore) */
  canShowRestore: boolean;

  /** Loading state while checking permissions */
  isLoading: boolean;
}

/** The seven matrix cells the hook resolves ('Button - Edit' mirrors 'Button - Save'). */
const BUTTON_FIELDS = [
  'Button - Save',
  'Button - Delete',
  'Button - Submit',
  'Button - Recording',
  'Button - Text Intake',
  'Button - Link Deal',
  'Button - Restore',
] as const;

const HIDDEN_BUTTON_PERMISSIONS: ButtonPermissions = {
  canShowSave: false,
  canShowEdit: false,
  canShowDelete: false,
  canShowSubmit: false,
  canShowRecording: false,
  canShowTextIntake: false,
  canShowLinkDeal: false,
  canShowRestore: false,
  isLoading: false,
};

/**
 * Optimistic first paint (#1469): compute all button grants synchronously from the
 * BUNDLED stage matrices, so the footer controls can render on the same frame the
 * request resolves instead of waiting out the permission API round trip.
 *
 * Client UX only — the async path in the effect below still fetches the API matrix and
 * re-commits whatever it says, and the server independently enforces every mutation. If
 * the bundled and served matrices disagree, the served one wins moments later.
 *
 * Returns `null` (caller stays on the fail-closed async path) whenever a synchronous
 * answer cannot match what the async path would conclude:
 *  - any per-role check comes back `resolved: false` (no matrix for the stage), or
 *  - the viewer is a Partner Contributor whose per-request access level is not already
 *    cached — that level gates a cross-role veto only the API can answer, and guessing
 *    would flash buttons in that the reconcile then removes.
 *
 * Text intake additionally needs the runtime `FEATURE_TEXT_INTAKE` switch (#1149). Only
 * the in-memory value is consulted here; before the first fetch it fails closed and the
 * async path reveals the button once the flag is known.
 */
const computeButtonPermissionsSync = (
  user: { roles: AppRole[]; email?: string },
  requestStage: string,
  requestId: string | undefined,
  viewerIsRequestor: boolean | undefined,
): ButtonPermissions | null => {
  // Seed the stage matrix from bundled JSON. Deliberately does NOT stamp the API-fetch
  // TTL, so loadPermissions() below still goes to the network and reconciles.
  fieldPermissionService.warmStagePermissionsCacheSync(requestStage);

  const isPartnerContributor = user.roles.includes(AppRole.PARTNER_CONTRIBUTOR);
  let partnerAccessLevel: string | undefined;
  if (isPartnerContributor && requestId && user.email) {
    const cacheKey = `${requestId}:${user.email}`;
    if (!teamInformationService.accessLevelCache?.has(cacheKey)) {
      return null;
    }
    partnerAccessLevel = teamInformationService.accessLevelCache.get(cacheKey);
  }
  const readOnlyPartnerOnThisRequest =
    isPartnerContributor &&
    partnerAccessLevel !== undefined &&
    partnerAccessLevel !== 'read_write';

  const checkButtonPermissionSync = (buttonName: string): boolean | null => {
    let unresolved = false;
    const roleChecks = user.roles.map(role => {
      // Mirrors the async checkButtonPermission: a non-read_write Partner Contributor
      // gets no action buttons from that role at all.
      if (
        role === AppRole.PARTNER_CONTRIBUTOR &&
        requestId &&
        user.email &&
        partnerAccessLevel !== 'read_write'
      ) {
        return false;
      }
      const access = fieldPermissionService.checkFieldAccessSync({
        userRoles: [role],
        section: 'Buttons',
        field: buttonName,
        requestStage,
        requestId,
        userEmail: user.email,
      });
      if (!access.resolved) {
        unresolved = true;
      }
      return access.canWrite === true;
    });
    if (unresolved) {
      return null;
    }
    // Same cross-role veto as the async path — see its comment for why a `read`
    // partner-contributor row overrides every role for a viewer who is not the creator.
    if (readOnlyPartnerOnThisRequest && viewerIsRequestor === false) {
      return false;
    }
    return roleChecks.some(hasPermission => hasPermission);
  };

  const results: boolean[] = [];
  for (const field of BUTTON_FIELDS) {
    const granted = checkButtonPermissionSync(field);
    if (granted === null) {
      return null;
    }
    results.push(granted);
  }
  const [save, del, submit, recording, textIntake, linkDeal, restore] = results;
  const textIntakeEnabled =
    runtimeFeatureFlagsService.peek()?.textIntake ?? false;
  return {
    canShowSave: save,
    canShowEdit: save, // Edit uses same permissions as Save
    canShowDelete: del,
    canShowSubmit: submit,
    canShowRecording: recording,
    canShowTextIntake: textIntake && textIntakeEnabled,
    canShowLinkDeal: linkDeal,
    canShowRestore: restore,
    isLoading: false,
  };
};

/**
 * Hook to check button-level permissions based on config files
 *
 * Uses the "Buttons" section in fieldPermissions.{STAGE}.json files
 * - W permission = show button (can click)
 * - Any other value (-, R, or missing) = hide button
 * - If user has multiple roles, shows button if ANY role has W
 *
 * @param requestStage - Current request stage (DRAFT, SITE_ASSESSMENT, etc.)
 * @param requestId - Optional request ID for debugging
 * @returns ButtonPermissions object with visibility flags for each button
 */
export const useButtonPermissions = (
  requestStage?: string,
  requestId?: string,
): ButtonPermissions => {
  const { user } = useAuth();
  const { isImpersonating, mode } = useImpersonation();
  const isReadOnlyImpersonation = isImpersonating && mode === 'read_only';

  /**
   * Is the viewer the request's creator?
   *
   * Read from the React Query cache rather than taken as a prop: this hook has five call
   * sites and `PermissionAwareButton` seven usages, none of which currently carry the
   * request, and threading it through all of them to answer one question is a lot of churn
   * for no extra correctness.
   *
   * `undefined` means "not cached, cannot tell" and is treated as today's behaviour below —
   * fail-open on the CLIENT only. The server is authoritative and now scopes permissions to
   * the roles that granted access, so an unknown here costs at most a button that appears
   * and then 403s, which is exactly what happens today.
   *
   * Uses the exported `queryClient` singleton rather than `useQueryClient()`. ⚠️ THE OLD
   * REASON GIVEN HERE WAS MEASURED FALSE, TWICE OVER (#1069 Story 6.2 code review,
   * 2026-09-16). It said the hook is consumed in places, and tests, not wrapped in a
   * `QueryClientProvider`. Neither half holds: `App.tsx` wraps the ENTIRE tree in
   * `<QueryClientProvider client={queryClient}>` with this same singleton, and every
   * consumer renders inside it (`PermissionAwareButton`'s render sites are all in
   * `RequestDetail.tsx`, which `AppContent` lazy-loads), so `useQueryClient()` would
   * return the identical client today; and the tests half ended with Story 6.1, since any
   * client suite can render through `src/test-utils/render` and get a provider and a
   * settled query (`.claude/rules/testing.md`, "The client timer harness: fresh legacy
   * fakes per test, capped at 100 ms").
   *
   * It stays because swapping a runtime cache read is out of scope for a story that
   * changes no behaviour, not because the singleton is required.
   *
   * ⚠️ DECIDED, 2026-09-17 (#1069 Story 6.5, D8): IT KEEPS THE SINGLETON, and the
   * hand-off Story 6.2 left open is closed. The swap changes no runtime behaviour, for
   * the reason above, and it would BREAK three suites that mount this hook bare, with no
   * provider and no mock -- `useQueryClient()` throws without one:
   * `src/hooks/__tests__/useButtonPermissions.test.ts`,
   * `src/hooks/__tests__/useButtonPermissions.partnerContributor.test.ts` and
   * `src/hooks/__tests__/deliverablesFallback.test.ts` (measured: zero
   * `QueryClientProvider` and zero `test-utils/render` hits between them). Nothing asks
   * for the change; a future story that wants it owns those three suites first.
   */
  const cachedRequest = requestId
    ? (queryClient.getQueryData(offlineRequestsKeys.detail(requestId)) as
        | { requestorEmail?: string }
        | undefined)
    : undefined;
  const viewerIsRequestor =
    cachedRequest?.requestorEmail && user?.email
      ? cachedRequest.requestorEmail.toLowerCase() === user.email.toLowerCase()
      : undefined;
  const [permissions, setPermissions] = useState<ButtonPermissions>({
    canShowSave: false,
    canShowEdit: false,
    canShowDelete: false,
    canShowSubmit: false,
    canShowRecording: false,
    canShowTextIntake: false,
    canShowLinkDeal: false,
    canShowRestore: false,
    isLoading: true,
  });

  /**
   * Identity of the permission question the CURRENT grants answer. When an effect re-run
   * asks the exact same question (same stage, request and viewer — typically a request
   * refetch swapping the `user` object reference), the previous grants are retained while
   * the answer refreshes in the background, instead of blanking every control and popping
   * it back in (#1469). The fail-closed reset is kept for a real change of stage, request
   * or viewer, where stale grants could briefly show another stage's controls.
   */
  const grantsIdentityRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const commitPermissions = (next: ButtonPermissions) => {
      if (!cancelled) {
        setPermissions(next);
      }
    };

    const grantsIdentity = [
      requestStage ?? '',
      requestId ?? '',
      user?.email ?? '',
      [...(user?.roles ?? [])].sort().join(','),
    ].join('|');
    const sameQuestionAsBefore = grantsIdentityRef.current === grantsIdentity;
    grantsIdentityRef.current = grantsIdentity;

    if (!sameQuestionAsBefore) {
      // Optimistic sync first paint from the bundled matrices (see
      // computeButtonPermissionsSync). Falls through to the fail-closed reset whenever a
      // synchronous answer is not available.
      let seededSynchronously = false;
      if (user?.roles && user.roles.length > 0 && requestStage) {
        try {
          const syncPermissions = computeButtonPermissionsSync(
            { roles: user.roles, email: user.email },
            requestStage,
            requestId,
            viewerIsRequestor,
          );
          if (syncPermissions) {
            setPermissions(syncPermissions);
            seededSynchronously = true;
          }
        } catch {
          // Unknown stage / partially-mocked service — the async path below decides.
        }
      }
      if (!seededSynchronously) {
        // Fail closed while a new user/request/stage permission set is loading. This
        // prevents action controls from briefly retaining the previous stage's grants.
        setPermissions({
          ...HIDDEN_BUTTON_PERMISSIONS,
          isLoading: true,
        });
      }
    }

    const loadPermissions = async () => {
      // If no user or no roles, hide all buttons
      if (!user?.roles || user.roles.length === 0) {
        commitPermissions({ ...HIDDEN_BUTTON_PERMISSIONS });
        return;
      }

      // If no stage provided, default to hiding buttons
      if (!requestStage) {
        commitPermissions({ ...HIDDEN_BUTTON_PERMISSIONS });
        return;
      }

      try {
        // Preload permissions for this stage
        await fieldPermissionService.loadPermissions(requestStage);

        /**
         * Resolved ONCE, outside the per-role loop, because the veto it drives applies
         * across roles rather than only to the Partner Contributor role. Also avoids
         * re-fetching the access level for every role of every button.
         */
        const partnerAccessLevel =
          user.roles.includes(AppRole.PARTNER_CONTRIBUTOR) &&
          requestId &&
          user.email
            ? await teamInformationService.getPartnerContributorAccessLevel(
                requestId,
                user.email,
              )
            : undefined;
        const readOnlyPartnerOnThisRequest =
          user.roles.includes(AppRole.PARTNER_CONTRIBUTOR) &&
          partnerAccessLevel !== undefined &&
          partnerAccessLevel !== 'read_write';

        // Check each button permission asynchronously to support partner contributor access levels
        const checkButtonPermission = async (
          buttonName: string,
        ): Promise<boolean> => {
          try {
            // Check if ANY of the user's roles has W permission for this button
            const roleChecks = await Promise.all(
              user.roles.map(async role => {
                // Special handling for Partner Contributors with request-specific access levels
                if (
                  role === AppRole.PARTNER_CONTRIBUTOR &&
                  requestId &&
                  user.email
                ) {
                  // Read-only Partner Contributors: no action buttons at all
                  if (partnerAccessLevel !== 'read_write') {
                    return false;
                  }

                  // Read-write Partner Contributors: fall through to config-based permission check
                  // Config files control what buttons they can see per stage
                }

                // For non-Partner Contributor roles, check normal permissions
                const access = await fieldPermissionService.checkFieldAccess({
                  userRoles: [role], // Check individual role
                  section: 'Buttons',
                  field: buttonName,
                  requestStage,
                  requestId,
                  userEmail: user.email,
                });

                // Show button only if permission is W (canWrite)
                return access.canWrite === true;
              }),
            );

            /**
             * A `read` partner-contributor row vetoes every role, not just its own.
             *
             * Mirrors the server, which now evaluates the button matrix only over the roles
             * that GRANTED access (`requestAuthorizationService.checkOwnership` ->
             * `authorizedRoles`). `External Requestor` authorizes only the viewer's OWN
             * request, so on someone else's it grants nothing — yet it is `W` on
             * `Button - Save` at DRAFT, SITE_ASSESSMENT and CONFIG, and `roleChecks.some()`
             * let it decide. Since every external user holds
             * `External Requestor + Partner Contributor` by the assignment rule, the per-role
             * rejection above vetoed nobody.
             *
             * Scoped to a viewer who is NOT the creator: an external requestor listed as a
             * read partner on their own request keeps the rights ownership gives them, which
             * is what the server concludes too. When we cannot tell (request not cached),
             * behave as before and let the server decide.
             */
            if (readOnlyPartnerOnThisRequest && viewerIsRequestor === false) {
              return false;
            }

            // Return true if ANY role has permission
            return roleChecks.some(hasPermission => hasPermission);
          } catch (error) {
            console.error(
              `Error checking permission for ${buttonName}:`,
              error,
            );
            return false; // Default to hiding button on error
          }
        };

        // All seven checks are independent reads of the same loaded matrix — run them in
        // parallel instead of serially awaiting each round trip (#1469). Safe with
        // Promise.all: checkButtonPermission catches internally and never rejects, and
        // runtimeFeatureFlagsService.get() never throws.
        const [
          [
            canShowSave,
            canShowDelete,
            canShowSubmit,
            canShowRecording,
            permittedTextIntake,
            canShowLinkDeal,
            canShowRestore,
          ],
          runtimeFlags,
        ] = await Promise.all([
          Promise.all(BUTTON_FIELDS.map(field => checkButtonPermission(field))),
          runtimeFeatureFlagsService.get(),
        ]);
        /**
         * Text intake is gated by the role matrix AND by a server-owned runtime
         * switch (#1149). The switch is the operator's kill control: the matrix
         * says who may use the feature, `FEATURE_TEXT_INTAKE` says whether this
         * deployment offers it at all. Both must agree, and the API enforces the
         * switch itself — this only keeps the UI from advertising a 503.
         */
        const canShowTextIntake =
          permittedTextIntake && runtimeFlags.textIntake;

        commitPermissions({
          canShowSave,
          canShowEdit: canShowSave, // Edit uses same permissions as Save
          canShowDelete,
          canShowSubmit,
          canShowRecording,
          canShowTextIntake,
          canShowLinkDeal,
          canShowRestore,
          isLoading: false,
        });
      } catch (error) {
        console.error('Error loading button permissions:', error);
        commitPermissions({ ...HIDDEN_BUTTON_PERMISSIONS });
      }
    };

    loadPermissions();
    return () => {
      cancelled = true;
    };
  }, [user, requestStage, requestId]);

  // Read-only impersonation: force all canShow flags to false (defense-in-depth)
  if (isReadOnlyImpersonation) {
    return {
      ...permissions,
      canShowSave: false,
      canShowEdit: false,
      canShowDelete: false,
      canShowSubmit: false,
      canShowRecording: false,
      canShowTextIntake: false,
      canShowRestore: false,
    };
  }

  return permissions;
};
